import type { ReactNode } from 'react';
import { Briefcase, ClipboardList, Coins, FileText, Forklift, Handshake, PackageSearch, ShieldCheck, TrendingUp, Wallet, Wrench } from 'lucide-react';
import type { ServiceOverview } from '@forq/service-data';
import type { SalesOverview } from '@forq/sales-data';

/**
 * Live-Kennzahlen fürs Dashboard. Wird in der App aus den Domänen-Gateways befüllt (nur was der User
 * darf); ein fehlendes Feld heißt „kein Recht / nicht geladen" und wird als „–" gezeigt, nicht als 0.
 */
export interface DashboardMetrics {
  readonly service?: ServiceOverview;
  readonly uvvDue?: number;
  readonly sales?: SalesOverview;
  readonly financeOpen?: number;
  readonly financeOverdue?: number;
  readonly financeOpenAmount?: number;
  readonly rentalActive?: number;
  readonly rentalRequests?: number;
  readonly rentalReturnDue?: number;
  readonly rentalBillingReady?: number;
  readonly warehouseStocked?: number;
  readonly warehouseLow?: number;
  readonly warehouseNegative?: number;
  readonly warehouseReplenish?: number;
}

export type DashboardProfileKey = 'alle' | 'vertrieb' | 'service' | 'finanzen' | 'vermietung' | 'lager';
export type DashboardKpiTone = 'normal' | 'warn' | 'danger';

export interface DashboardKpi {
  readonly key: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly route: string;
  readonly value: (metrics: DashboardMetrics) => string;
  readonly note?: (metrics: DashboardMetrics) => string;
  readonly tone?: (metrics: DashboardMetrics) => DashboardKpiTone;
}

export interface DashboardProfile {
  readonly key: DashboardProfileKey;
  readonly label: string;
  readonly icon: ReactNode;
  /** Rechte, die dieses Profil freischalten (mind. eines). null = immer sichtbar („Alle"). */
  readonly permissions: readonly string[] | null;
  /** Bevorzugte Reihenfolge der Schnellzugriff-Kacheln (Kachel-Keys). Rest folgt danach. */
  readonly actionOrder: readonly string[];
  readonly kpis: readonly DashboardKpi[];
}

const eur0 = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const count = (value: number | undefined): string => (value === undefined ? '–' : String(value));

// KPI-Bausteine (wiederverwendet über Profile hinweg).
const kpiOpenService: DashboardKpi = {
  key: 'open-service', label: 'Offene Vorgänge', icon: <ClipboardList size={18} />, route: '/service',
  value: (m) => (m.service ? String(m.service.planningCount + m.service.issuedCount) : '–'),
  note: () => 'Planung + ausgegeben',
};
const kpiBillingReady: DashboardKpi = {
  key: 'billing-ready', label: 'Bereit zur Rechnung', icon: <FileText size={18} />, route: '/finance/service-vorschlaege',
  value: (m) => count(m.service?.billingReadyCount),
  tone: (m) => ((m.service?.billingReadyCount ?? 0) > 0 ? 'warn' : 'normal'),
};
const kpiApprovals: DashboardKpi = {
  key: 'approvals', label: 'Freigaben offen', icon: <ShieldCheck size={18} />, route: '/service',
  value: (m) => count(m.service?.approvalPendingCount),
};
const kpiUvv: DashboardKpi = {
  key: 'uvv-due', label: 'UVV fällig', icon: <ShieldCheck size={18} />, route: '/service',
  value: (m) => count(m.uvvDue),
  note: () => 'Prüftermine',
  tone: (m) => ((m.uvvDue ?? 0) > 0 ? 'danger' : 'normal'),
};
const kpiOpenOffers: DashboardKpi = {
  key: 'open-offers', label: 'Offene Angebote', icon: <Handshake size={18} />, route: '/verkauf',
  value: (m) => count(m.sales?.openOffers),
};
const kpiFollowUps: DashboardKpi = {
  key: 'follow-ups', label: 'Follow-ups fällig', icon: <Briefcase size={18} />, route: '/verkauf',
  value: (m) => count(m.sales?.followUpsDue),
  tone: (m) => ((m.sales?.followUpsDue ?? 0) > 0 ? 'warn' : 'normal'),
};
const kpiPipeline: DashboardKpi = {
  key: 'pipeline', label: 'Pipeline', icon: <TrendingUp size={18} />, route: '/verkauf',
  value: (m) => (m.sales ? eur0.format(m.sales.pipelineValue) : '–'),
  note: () => 'gewichtet offen',
};
const kpiHandoffs: DashboardKpi = {
  key: 'handoffs', label: 'Übergaben offen', icon: <Handshake size={18} />, route: '/verkauf',
  value: (m) => count(m.sales?.pendingHandoffs),
};
const kpiOpenPosten: DashboardKpi = {
  key: 'open-posten', label: 'Offene Posten', icon: <Wallet size={18} />, route: '/finance/offene-posten',
  value: (m) => count(m.financeOpen),
};
const kpiOverdue: DashboardKpi = {
  key: 'overdue', label: 'Überfällig', icon: <Coins size={18} />, route: '/finance/mahnwesen',
  value: (m) => count(m.financeOverdue),
  tone: (m) => ((m.financeOverdue ?? 0) > 0 ? 'danger' : 'normal'),
};
const kpiOpenAmount: DashboardKpi = {
  key: 'open-amount', label: 'Offenes Volumen', icon: <Coins size={18} />, route: '/finance/offene-posten',
  value: (m) => (m.financeOpenAmount === undefined ? '–' : eur0.format(m.financeOpenAmount)),
};
const kpiRentalActive: DashboardKpi = {
  key: 'rental-active', label: 'Aktive Mieten', icon: <Forklift size={18} />, route: '/mietpark',
  value: (m) => count(m.rentalActive), note: () => 'laufend',
};
const kpiRentalRequests: DashboardKpi = {
  key: 'rental-requests', label: 'Anfragen offen', icon: <Handshake size={18} />, route: '/mietpark',
  value: (m) => count(m.rentalRequests),
  tone: (m) => ((m.rentalRequests ?? 0) > 0 ? 'warn' : 'normal'),
};
const kpiRentalReturns: DashboardKpi = {
  key: 'rental-returns', label: 'Rückholung geplant', icon: <ClipboardList size={18} />, route: '/mietpark',
  value: (m) => count(m.rentalReturnDue),
};
const kpiRentalBilling: DashboardKpi = {
  key: 'rental-billing', label: 'Abrechnung offen', icon: <Coins size={18} />, route: '/mietpark',
  value: (m) => count(m.rentalBillingReady),
  tone: (m) => ((m.rentalBillingReady ?? 0) > 0 ? 'warn' : 'normal'),
};
const kpiStockLow: DashboardKpi = {
  key: 'stock-low', label: 'Unter Mindestbestand', icon: <PackageSearch size={18} />, route: '/lager',
  value: (m) => count(m.warehouseLow),
  tone: (m) => ((m.warehouseLow ?? 0) > 0 ? 'warn' : 'normal'),
};
const kpiStockReplenish: DashboardKpi = {
  key: 'stock-replenish', label: 'Beschaffung offen', icon: <ClipboardList size={18} />, route: '/lager',
  value: (m) => count(m.warehouseReplenish),
};
const kpiStockNegative: DashboardKpi = {
  key: 'stock-negative', label: 'Negativ-Bestände', icon: <PackageSearch size={18} />, route: '/lager',
  value: (m) => count(m.warehouseNegative),
  tone: (m) => ((m.warehouseNegative ?? 0) > 0 ? 'danger' : 'normal'),
};
const kpiStocked: DashboardKpi = {
  key: 'stocked', label: 'Artikel mit Bestand', icon: <PackageSearch size={18} />, route: '/lager',
  value: (m) => count(m.warehouseStocked),
};

export const DASHBOARD_PROFILES: readonly DashboardProfile[] = [
  {
    key: 'alle', label: 'Alle', icon: <ClipboardList size={16} />, permissions: null,
    actionOrder: ['service', 'invoice', 'customer', 'machine', 'rental', 'calendar'],
    kpis: [kpiOpenService, kpiBillingReady, kpiOverdue, kpiUvv, kpiOpenOffers],
  },
  {
    key: 'vertrieb', label: 'Vertrieb', icon: <Handshake size={16} />, permissions: ['sales.view'],
    actionOrder: ['customer', 'machine', 'invoice', 'service', 'calendar', 'rental'],
    kpis: [kpiOpenOffers, kpiFollowUps, kpiPipeline, kpiHandoffs],
  },
  {
    key: 'service', label: 'Service', icon: <Wrench size={16} />, permissions: ['service.view', 'orders.view'],
    actionOrder: ['service', 'machine', 'customer', 'invoice', 'calendar', 'rental'],
    kpis: [kpiOpenService, kpiUvv, kpiApprovals, kpiBillingReady],
  },
  {
    key: 'finanzen', label: 'Finanzen', icon: <Wallet size={16} />, permissions: ['finance.view'],
    actionOrder: ['invoice', 'customer', 'service', 'machine', 'rental', 'calendar'],
    kpis: [kpiOpenPosten, kpiOverdue, kpiOpenAmount, kpiBillingReady],
  },
  {
    key: 'vermietung', label: 'Vermietung', icon: <Forklift size={16} />, permissions: ['rental.view'],
    actionOrder: ['rental', 'calendar', 'machine', 'customer', 'invoice', 'service'],
    kpis: [kpiRentalActive, kpiRentalRequests, kpiRentalReturns, kpiRentalBilling],
  },
  {
    key: 'lager', label: 'Lager', icon: <PackageSearch size={16} />, permissions: ['warehouse.view'],
    actionOrder: ['machine', 'service', 'customer', 'invoice', 'rental', 'calendar'],
    kpis: [kpiStockLow, kpiStockReplenish, kpiStockNegative, kpiStocked],
  },
];

/** Profile, die der User laut Rechten sehen darf („Alle" immer). */
export function availableProfiles(permissions: { has(permission: string): boolean }): readonly DashboardProfile[] {
  return DASHBOARD_PROFILES.filter(
    (profile) => profile.permissions === null || profile.permissions.some((permission) => permissions.has(permission)),
  );
}

/**
 * Automatisch erkanntes Standard-Profil: Admins/GF starten auf „Alle" (Gesamtüberblick), Fachrollen auf
 * ihrem ersten fachlichen Profil.
 */
export function defaultProfile(permissions: { has(permission: string): boolean }): DashboardProfileKey {
  if (permissions.has('admin.view')) return 'alle';
  const specific = availableProfiles(permissions).find((profile) => profile.key !== 'alle');
  return specific?.key ?? 'alle';
}

export function profileByKey(key: string): DashboardProfile {
  return DASHBOARD_PROFILES.find((profile) => profile.key === key) ?? DASHBOARD_PROFILES[0];
}
